import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const PaybackPeriodSection = () => {
  const solutions = [
    {
      name: 'Zonnepanelen',
      icon: 'Sun',
      investment: '€5.000 - €12.000',
      yearlyReturn: '€600 - €1.200',
      payback: '7-10 jaar',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-50'
    },
    {
      name: 'Warmtepomp',
      icon: 'Thermometer',
      investment: '€10.000 - €18.000',
      yearlyReturn: '€800 - €1.500',
      payback: '10-15 jaar',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50'
    },
    {
      name: 'Isolatie',
      icon: 'Home',
      investment: '€3.000 - €8.000',
      yearlyReturn: '€400 - €800',
      payback: '7-12 jaar',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Thuisbatterij',
      icon: 'Battery',
      investment: '€6.000 - €10.000',
      yearlyReturn: '€500 - €900',
      payback: '10-15 jaar',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Terugverdientijd per oplossing
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Vergelijk de terugverdientijd van verschillende verduurzamingsoplossingen
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {solutions?.map((solution, index) => (
            <motion.div
              key={solution?.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              className={`${solution?.bgColor} rounded-2xl p-6 shadow-elevation-2 border border-gray-200`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${solution?.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon name={solution?.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{solution?.name}</h3>
                </div>
              </div>

              {/* Investment */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Investering</span>
                  <Icon name="Euro" size={16} className="text-muted-foreground" />
                </div>
                <p className="text-2xl font-bold text-foreground">{solution?.investment}</p>
              </div>

              {/* Yearly Return */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Jaarlijkse besparing</span>
                  <Icon name="TrendingDown" size={16} className="text-success" />
                </div>
                <p className="text-2xl font-bold text-success">{solution?.yearlyReturn}</p>
              </div>

              {/* Payback Period */}
              <div className="bg-white rounded-xl p-4 shadow-elevation-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Terugverdientijd</span>
                  <Icon name="Clock" size={16} className="text-primary" />
                </div>
                <p className="text-3xl font-bold text-primary">{solution?.payback}</p>
              </div>

              {/* Timeline Visualization */}
              <div className="mt-6">
                <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: index * 0.2 }}
                    className={`absolute top-0 left-0 h-full bg-gradient-to-r ${solution?.color} rounded-full`}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>Start</span>
                  <span>Terugverdiend</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-4xl mx-auto"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Icon name="Info" size={24} className="text-blue-600" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold text-foreground mb-2">
                Let op: individuele situatie verschilt
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                De terugverdientijd hangt af van veel factoren zoals uw energieverbruik, woningtype, 
                oriëntatie, isolatiegraad en energieprijzen. Gebruik onze calculator voor een 
                persoonlijke berekening of vraag een offerte aan voor een exacte inschatting.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PaybackPeriodSection;